import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  eyebrow?: string;
  headline: string;
  faqs: FAQItem[];
}

export default function SectionFAQ({ eyebrow = 'Frequently Asked', headline, faqs }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, inView } = useInView();

  return (
    <section id="faq" className="bg-[#F9F7F4] py-28 md:py-36">
      <div className="max-w-4xl mx-auto px-6">
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`mb-14 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#00B4D8]" />
            <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">
              {eyebrow}
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#0D1B2A] leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {headline}
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`bg-white rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${
                openIndex === i ? 'ring-1 ring-[#00B4D8]/35' : 'hover:ring-1 hover:ring-[#0D1B2A]/10'
              }`}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <div className="flex items-center justify-between gap-4 px-7 py-5">
                <h4
                  className="text-[#0D1B2A] font-semibold text-[15px] leading-snug"
                  id={`faq-${i}`}
                >
                  <a
                    href={`#faq-${i}`}
                    onClick={(e) => e.preventDefault()}
                    className="hover:text-[#00B4D8] transition-colors"
                  >
                    {faq.question}
                  </a>
                </h4>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${
                    openIndex === i
                      ? 'bg-[#00B4D8] text-white rotate-45'
                      : 'bg-[#F9F7F4] text-[#0D1B2A]'
                  }`}
                >
                  <i className="ri-add-line text-sm" />
                </div>
              </div>

              {openIndex === i && (
                <div className="px-7 pb-6">
                  <div className="w-8 h-px bg-[#00B4D8]/35 mb-4" />
                  <p className="text-[#687585] text-[15px] leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
